import { RiSearchEyeLine, RiBarChartGroupedLine, RiShieldCheckLine, RiRocket2Line } from "react-icons/ri";
import { FiSettings, FiKey, FiFileText, FiLink } from "react-icons/fi";
import {
  LuSettings,
  LuUsers,
  LuCircleCheck,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const SeoAppHeroData = {
  backgroundImage: "/images/serviceBanners/SEOContent.webp",
  whiteImage: "/images/serviceBanners/SEOW.webp",
  subTitle: "SEO & Content Strategy Services",
  title: (
    <>
      Dominate Search Rankings with <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Strategic SEO</span>
    </>
  ),
  description:
    "We build structured SEO content strategies that improve search rankings, optimize Core Web Vitals, and strengthen technical SEO to drive sustainable organic growth, higher visibility, and consistent traffic for businesses.",
  primaryButton: {
    title: "Get a Free SEO Audit",
    onClick: () => console.log("SEO Audit clicked"),
  },
  secondaryButton: {
    title: "View Our Rankings",
    onClick: () => console.log("SEO Results clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "Organic Visibility",
      desc: "Improve search rankings through sustainable organic growth.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Technical SEO",
      desc: "Optimized Core Web Vitals, schema markup, and site performance.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Content Authority",
      desc: "Strategic content structures designed to build topical relevance.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Sustainable Growth",
      desc: "Long-term SEO strategies that reduce customer acquisition costs.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp", // Generic technical logic icon
      top: "16%",
      right: "8%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};
// 2. Performance Stats Block
export const seoStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block
export const seoSolutionsAndProcessData = {
  buildSubtitle: "HOW WE RANK",
  buildTitle: "Data-Driven SEO Strategies for Sustainable Organic Growth",
  buildCards: [
    {
      title: "Technical SEO Audits",
      desc: "Comprehensive audits focused on crawlability, indexing, and technical site performance.",
      icon: FiSettings,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Keyword Intelligence",
      desc: "Intent-driven keyword research and clustering to target the right audience.",
      icon: FiKey,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Content Strategy",
      desc: "SEO-focused content plans designed to improve rankings and conversions.",
      icon: FiFileText,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Authority Building",
      desc: "Strategic link building and internal SEO to strengthen domain authority.",
      icon: FiLink,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "Content Planning Framework",
      desc: "Structured roadmap to organize topics, topical clusters, and publishing strategy.",
      icon: FiFileText,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Conversion-Focused SEO",
      desc: "Optimize content elements to increase high-value leads, user engagement, and business conversions.",
      icon: FiSettings,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR SEO METHODOLOGY",
  processTitle: "Our Strategic SEO Growth Roadmap",
  processSteps: [
    {
      step: "01",
      title: "Technical Audit",
      description: "Identify and resolve technical issues affecting search visibility.",
      icon: LuUsers,
    },
    {
      step: "02",
      title: "Content Strategy",
      description: "Build a keyword-focused content roadmap based on search intent.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Optimization",
      description: "Improve on-page and off-page SEO to strengthen authority.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Performance Tracking",
      description: "Monitor rankings, traffic, and conversions to support continuous growth.",
      icon: LuRocket,
    },
    {
      step: "05",
      title: "On-Page SEO Enhancement",
      description: "Optimize meta tags, headings, and content structure for better ranking optimization.",
      icon: LuCircleCheck, // Replaced with compile-safe alternative to prevent Next.js build bottlenecks
    },
    {
      step: "06",
      title: "Link Authority Building",
      description: "Strengthen domain authority through highly ethical, high-quality backlink strategies.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY BUSINESSES CHOOSE OUR SEO STRATEGY",
  benefitsTitle: "Building Long-Term Search Authority",
  benefits: [
    "Advanced technical SEO and site speed optimization",
    "Strategic content clustering for stronger topical relevance",
    "Ethical link building to strengthen domain authority",
    "Core Web Vitals and structured schema optimization",
    "Search intent mapping focused on qualified conversions",
  ],
  logoImage: "/images/serviceBanners/SEOContent.webp",
  whiteLogoImage: "/images/serviceBanners/SEOW.webp",
};

// 4. Portfolio / Case Studies Data
export const seoPortfolioData = {
  subtitle: "OUR RESULTS",
  title: "Organic Rankings That Drive Revenue",
  buttonText: "View Case Studies",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "SaaS Leader",
      badge: "Organic Growth",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Scaling organic traffic by 400% in 6 months through technical fixing and content silos.",
      downloads: "+400% Traffic",
      rating: "5.0",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2070&auto=format&fit=crop",
    },
    {
      title: "B2B Service",
      badge: "Authority Build",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "Establishing Page 1 dominance for 50+ high-competition industry keywords.",
      downloads: "50+ Top 3 Keys",
      rating: "4.9",
      image: "https://images.unsplash.com/photo-1558403194-611308249627?q=80&w=2070&auto=format&fit=crop",
    },
  ],
};

// 5. Technologies Data
export const seoTechnologiesData = {
  techTitle: "OUR SEO TOOL STACK",
  technologies: [
    { name: "Search Console", icon: "Search" },
    { name: "Screaming Frog", icon: "Bug" },
    { name: "Indexing API", icon: "RestAPI" },
    { name: "Schema Tools", icon: "Provider" },
    { name: "Speed Optimization", icon: "Vite" },
  ],
  trustTitle: "TRUSTED BY GOOGLE PARTNERS",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google_logo" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft_logo" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon_logo" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe_logo" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm_logo" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle_logo" },
  ],
};

// 6. About Overview
export const seoAboutData = [
  {
    title: "SEO Services Company Focused on Sustainable Growth",
    content: (
      <>
        Businesses looking for long-term visibility often struggle with rising advertising costs and inconsistent traffic. A reliable{" "}
        <b className="text-text-primary font-semibold">seo services company</b> helps brands build sustainable growth by improving visibility through structured optimization
        instead of short-term wins. Modern SEO is no longer limited to rankings alone. It requires stronger technical performance, content relevance, and audience intent mapping.
        At <b className="text-text-primary font-semibold">Tycho Technologies</b>, businesses receive customized strategies designed to improve discoverability while supporting
        measurable growth across competitive industries.
      </>
    ),
  },
  {
    title: "Search Engine Optimization Services That Support Business Goals",
    content: (
      <>
        Effective <b className="text-text-primary font-semibold">search engine optimization services</b> focus on attracting qualified visitors rather than increasing traffic
        numbers alone. A trusted <b className="text-text-primary font-semibold">seo agency for businesses</b> understands how user intent, content structure, and technical
        performance influence rankings. Companies investing in strategic SEO often experience stronger lead quality, better engagement, and improved conversion opportunities over
        time. Tycho Technologies works with brands to create practical SEO systems that align with business objectives while adapting to changing search behavior in 2026.
      </>
    ),
  },
  {
    title: "SEO Content Marketing Services for Better Authority",
    content: (
      <>
        Strong rankings are built through useful, experience-driven content that solves real user problems. High-performing{" "}
        <b className="text-text-primary font-semibold">seo content marketing services</b> help businesses strengthen topical authority while improving trust signals for search
        engines. Content ecosystems designed around customer questions often create stronger organic momentum than isolated pages. Businesses seeking stronger visibility frequently
        combine SEO strategies with{" "}
        <a href="https://tychotechnologies.com/social-media-marketing-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          social media marketing services
        </a>{" "}
        to improve brand awareness while building consistent digital authority.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a SEO Company in India",
    content: (
      <>
        Demand for a reliable <b className="text-text-primary font-semibold">seo company in india</b> continues to grow as businesses seek cost-effective and scalable growth
        strategies. Brands want experienced teams that understand algorithm updates, content relevance, and technical optimization without relying on shortcuts. Companies working
        with Tycho Technologies benefit from tailored planning, transparent reporting, and long-term execution strategies. Many organizations also align SEO efforts with an{" "}
        <a href="https://tychotechnologies.com/growth-analytics-ai-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          AI analytics company in india
        </a>{" "}
        to improve performance tracking and data-backed decision-making.
      </>
    ),
  },
];
// 7. FAQs
export const seoFaqData = [
  {
    q: "Why should I invest in SEO services in India for my business?",
    a: "SEO services in India help businesses improve their online visibility, attract more website visitors, and generate leads. Skilled Indian SEO experts offer cost-effective strategies for ranking higher on Google and reaching your target audience efficiently.",
  },
  {
    q: "How can an SEO company in Delhi/NCR help my business grow?",
    a: "An SEO company in Delhi/NCR provides customized strategies to boost your website’s search engine rankings. They offer services like keyword research, on-page and off-page optimization, content strategy, and analytics tracking to help your business reach local and national audiences.",
  },
  {
    q: "Why is SEO important for small businesses?",
    a: "SEO helps small businesses compete with larger companies by improving online visibility, driving targeted traffic, and increasing leads. Affordable SEO strategies ensure small businesses can reach the right customers and grow without huge marketing budgets.",
  },
  {
    q: "What is local SEO and why does my business need it?",
    a: "Local SEO focuses on optimizing your website for nearby customers. It helps your business appear in local searches, Google Maps, and “near me” queries, making it easier for people in your area to find your products or services.",
  },
];
